import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Printer, BookOpen, AlertTriangle, CheckSquare, Save, Download, Upload, Languages, FileDown, Link, FileText, Cloud, DownloadCloud, X, RefreshCw } from 'lucide-react';
import commandsData from './commandsData.json';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType, AlignmentType } from "docx";
import { saveAs } from "file-saver";
import {
    collection,
    addDoc,
    onSnapshot,
    query,
    serverTimestamp,
    doc,
    deleteDoc,
    getDoc,
    setDoc,
    where,
    getDocs,
    updateDoc,
    Timestamp
} from 'firebase/firestore';

// Definição da Paleta de Cores baseada na imagem enviada
const palette = {
    green: '#9fce9c',
    purple: '#c7a0cb',
    yellow: '#fbdb35',
    blue: '#95b6df',
    mutedPurple: '#c8c3d8',
    orange: '#f0a43e',
    paleGreen: '#cce5d0',
    paleYellow: '#ffe37f',
    paleBlue: '#bad2ed',
    teal: '#a6cdd0',
    peach: '#fbc481',
    lavender: '#c9c4d8',
};

// Dicionário de Traduções (Labels do PDF)
const translations = {
    pt: {
        labelTitle: "Título do Ciclo",
        labelSubject: "Disciplina",
        headerStudyGuide: "Study Guide",
        sectionSkills: "Objetivos de Aprendizagem",
        sectionResources: "Prática & Recursos",
        sectionPitfall: "Atenção / Ponto de Erro",
        sectionNotes: "Espaço para anotações / Active Recall",
        footer: "Study Guide gerado para uso pessoal. Bom estudo!",
        defaultResources: "Nenhum recurso listado.",
        editorTitle: "Editor do Study Guide",
        editorSubtitle: "Preencha os dados para gerar o PDF.",
        btnNewTopic: "Novo Tópico",
        btnPrint: "Imprimir",
        btnDownload: "Baixar PDF",
        lblLanguage: "Idioma do Study Guide",
        placeholderTopic: "Nome do Tópico (ex: Termodinâmica)",
        placeholderSkill: "Habilidade...",
        placeholderRes: "Ex: Livro pág 45, Lista extra ex. 2...",
        placeholderPitfall: "Aviso sobre erros frequentes...",
        lblChecklist: 'Checklist "Eu consigo..."',
        lblResources: "Onde Estudar (Recursos)",
        lblPitfall: "Alerta de Erro Comum",
        lblSuggestedPractice: "Suggested Practice",
        btnDoc: "Baixar DOC",
        downloading: "Gerando PDF..."
    },
    en: {
        labelTitle: "Cycle Title",
        labelSubject: "Subject",
        headerStudyGuide: "Study Guide",
        sectionSkills: "Learning Objectives",
        sectionResources: "Practice & Resources",
        sectionPitfall: "Warning / Common Pitfall",
        sectionNotes: "Notes / Active Recall Space",
        footer: "Study Guide generated for personal use. Happy studying!",
        defaultResources: "No resources listed.",
        editorTitle: "Guide Editor",
        editorSubtitle: "Fill in the data to generate the PDF.",
        btnNewTopic: "New Topic",
        btnPrint: "Print",
        btnDownload: "Download PDF",
        lblLanguage: "Guide Language",
        placeholderTopic: "Topic Name (e.g., Thermodynamics)",
        placeholderSkill: "Skill...",
        placeholderRes: "E.g., Book pg 45, Extra List ex. 2...",
        placeholderPitfall: "Warning about common mistakes...",
        lblChecklist: 'Learning Objectives',
        lblResources: "Where to Study (Resources)",
        lblPitfall: "Common Pitfall Alert",
        lblSuggestedPractice: "Suggested Practice",
        btnDoc: "Download DOC",
        downloading: "Generating PDF..."
    }
};

// Dados iniciais de exemplo
// Dados iniciais LIMPOS (Reset a cada montagem)
// Dicionário de Traduções (Labels do PDF)
// ...
// Dados iniciais LIMPOS (Reset a cada montagem)
const initialData = {
    title: "",
    subject: "",
    grade: "", // Nova Série
    topics: [],
    selectedCommands: []
};

const latexSymbols = [
    { label: 'x²', code: '$x^2$' },
    { label: '√', code: '$\\sqrt{x}$' },
    { label: 'π', code: '$\\pi$' },
    { label: 'α', code: '$\\alpha$' },
    { label: 'β', code: '$\\beta$' },
    { label: 'θ', code: '$\\theta$' },
    { label: 'Δ', code: '$\\Delta$' },
    { label: 'Frac', code: '$\\frac{a}{b}$' },
    { label: '≈', code: '$\\approx$' },
    { label: '≠', code: '$\\neq$' },
    { label: '→', code: '$\\rightarrow$' },
    { label: '⋅', code: '$\\cdot$' },
];

const SUBJECTS_MAPPING = [
    { pt: "Matemática", en: "Mathematics" },
    { pt: "Química", en: "Chemistry" },
    { pt: "Física", en: "Physics" },
    { pt: "Biologia", en: "Biology" },
    { pt: "Ciências", en: "Science" },
    { pt: "História", en: "History" },
    { pt: "Geografia", en: "Geography" },
    { pt: "Filosofia", en: "Philosophy" },
    { pt: "Sociologia", en: "Sociology" },
    { pt: "Language Arts", en: "Language Arts" },
    { pt: "Língua Portuguesa", en: "Portuguese Language" },
];

const StudyGuideGenerator = ({ db, auth, currentUser, appId, activeSubject }) => {
    const [guideData, setGuideData] = useState({
        ...initialData,
        subject: activeSubject || ""
    });
    const [selectedCmdId, setSelectedCmdId] = useState('');
    const [customCmdName, setCustomCmdName] = useState('');
    const [lang, setLang] = useState('pt');
    const [isGenerating, setIsGenerating] = useState(false);
    const [lastFocusedInput, setLastFocusedInput] = useState(null);
    
    // DB States
    const [savedGuides, setSavedGuides] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    
    // Filter States
    const [filterGrade, setFilterGrade] = useState('');
    const [filterSubject, setFilterSubject] = useState(activeSubject || '');

    const t = translations[lang];

    // Rastreia o último input focado para inserir símbolos
    useEffect(() => {
        const handleFocus = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                setLastFocusedInput(e.target);
            }
        };
        document.addEventListener('focusin', handleFocus);
        return () => document.removeEventListener('focusin', handleFocus);
    }, []);

    // --- Integração com Firebase (Simplificada e Coerente) ---

    // Escutar Banco de Dados (Tempo Real para toda a escola)
    useEffect(() => {
        if (!db || !appId) return;
        setIsLoadingHistory(true);
        const q = collection(db, 'artifacts', appId, 'public', 'data', 'study_guides');
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const guides = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSavedGuides(guides.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
            setIsLoadingHistory(false);
        });
        return () => unsubscribe();
    }, [db, appId]);

    const insertLatex = (latex) => {
        if (!lastFocusedInput) {
            alert(lang === 'pt' ? "Clique em um campo de texto primeiro!" : "Click on a text field first!");
            return;
        }

        const input = lastFocusedInput;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const text = input.value;
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);
        const newText = before + latex + after;

        // Update input value naturally so React picks it up
        const isTextArea = input.tagName === 'TEXTAREA';
        const proto = isTextArea ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(proto, "value").set;
        nativeInputValueSetter.call(input, newText);

        const event = new Event('input', { bubbles: true });
        input.dispatchEvent(event);

        // Restore focus and cursor
        input.focus();
        const newCursorPos = start + latex.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
    };

    // Switch Language and update commands
    const handleSetLang = (newLang) => {
        setLang(newLang);

        // Localize Subject
        const currentSubject = guideData.subject;
        const mapping = SUBJECTS_MAPPING.find(m => m.pt === currentSubject || m.en === currentSubject);
        if (mapping) {
            setGuideData(prev => ({ ...prev, subject: mapping[newLang] }));
        }

        // Update existing selected commands to the new language
        if (guideData.selectedCommands) {
            const updatedCommands = guideData.selectedCommands.map(cmd => {
                const original = commandsData.find(c => c.id === cmd.id);
                if (!original) return cmd;

                return {
                    ...cmd,
                    verb: newLang === 'en' ? original.verbEn : original.verb,
                    level: newLang === 'en' ? original.levelEn : original.level,
                    definition: newLang === 'en' ? original.definitionEn : original.definition,
                    howToAnswer: newLang === 'en' ? original.howToAnswerEn : original.howToAnswer
                };
            });
            setGuideData(prev => ({ ...prev, selectedCommands: updatedCommands }));
        }
    };

    // --- Funções de Manipulação de Estado ---

    const handleNewGuide = () => {
        if (window.confirm(lang === 'pt' ? 'Criar um novo Study Guide? O guia atual será removido da tela.' : 'Create a new Study Guide? Current work will be removed from view.')) {
            setGuideData({
                ...initialData,
                subject: activeSubject || ""
            });
            setEditingId(null);
            setLang('pt');
        }
    };

    const saveStudyGuide = async () => {
        if (!db || !appId) return;
        if (!guideData.title || !guideData.grade) {
            alert(lang === 'pt' ? "Por favor, preencha o Título e a Série." : "Please fill in Title and Grade.");
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = {
                ...guideData,
                timestamp: serverTimestamp(),
                updatedBy: currentUser?.email || 'anonymous'
            };

            if (editingId) {
                const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'study_guides', editingId);
                await setDoc(docRef, dataToSave, { merge: true });
                alert(lang === 'pt' ? "Guide atualizado com sucesso!" : "Guide updated successfully!");
            } else {
                const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'study_guides'), dataToSave);
                setEditingId(docRef.id);
                alert(lang === 'pt' ? "Guide salvo com sucesso!" : "Guide saved successfully!");
            }
        } catch (err) {
            console.error(err);
            alert("Erro ao salvar: " + err.message);
        } finally {
            setIsSaving(true); // Wait, should be false
            setIsSaving(false);
        }
    };

    const loadStudyGuide = (guide) => {
        setGuideData({
            title: guide.title || "",
            subject: guide.subject || "",
            grade: guide.grade || "",
            topics: guide.topics || [],
            selectedCommands: guide.selectedCommands || []
        });
        setEditingId(guide.id);
        setShowHistory(false);
    };

    const deleteStudyGuide = async (id) => {
        if (!window.confirm(lang === 'pt' ? 'Tem certeza que deseja excluir? Esta ação é irreversível.' : 'Are you sure? This action cannot be undone.')) return;
        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'study_guides', id));
            if (editingId === id) handleNewGuide();
        } catch (err) {
            alert("Erro ao excluir: " + err.message);
        }
    };

    const updateField = (field, value) => {
        setGuideData({ ...guideData, [field]: value });
    };

    const addTopic = () => {
        const newTopic = {
            id: Date.now(),
            title: lang === 'pt' ? "Novo Tópico" : "New Topic",
            skills: [lang === 'pt' ? "Nova habilidade..." : "New skill..."],
            resources: "",
            suggestedPractice: "",
            pitfall: ""
        };
        setGuideData({ ...guideData, topics: [...guideData.topics, newTopic] });
    };

    const removeTopic = (id) => {
        setGuideData({ ...guideData, topics: guideData.topics.filter(t => t.id !== id) });
    };

    const updateTopic = (id, field, value) => {
        const newTopics = guideData.topics.map(t => {
            if (t.id === id) return { ...t, [field]: value };
            return t;
        });
        setGuideData({ ...guideData, topics: newTopics });
    };

    const addSkill = (topicId) => {
        const newTopics = guideData.topics.map(t => {
            if (t.id === topicId) return { ...t, skills: [...t.skills, ""] };
            return t;
        });
        setGuideData({ ...guideData, topics: newTopics });
    };

    const updateSkill = (topicId, skillIndex, value) => {
        const newTopics = guideData.topics.map(t => {
            if (t.id === topicId) {
                const newSkills = [...t.skills];
                newSkills[skillIndex] = value;
                return { ...t, skills: newSkills };
            }
            return t;
        });
        setGuideData({ ...guideData, topics: newTopics });
    };

    const removeSkill = (topicId, skillIndex) => {
        const newTopics = guideData.topics.map(t => {
            if (t.id === topicId) {
                const newSkills = t.skills.filter((_, index) => index !== skillIndex);
                return { ...t, skills: newSkills };
            }
            return t;
        });
        setGuideData({ ...guideData, topics: newTopics });
    };

    const handleAddCommand = () => {
        if (!selectedCmdId) return;

        if (selectedCmdId === 'custom') {
            if (!customCmdName.trim()) return;
            const commandToAdd = {
                id: 'custom-' + Date.now(),
                verb: customCmdName.trim(),
                level: lang === 'pt' ? 'Personalizado' : 'Custom',
                definition: '',
                howToAnswer: ''
            };
            setGuideData({
                ...guideData,
                selectedCommands: [...(guideData.selectedCommands || []), commandToAdd]
            });
            setSelectedCmdId('');
            setCustomCmdName('');
            return;
        }

        if ((guideData.selectedCommands || []).some(cmd => cmd.id === selectedCmdId)) return;

        const originalCmd = commandsData.find(c => c.id === selectedCmdId);
        if (originalCmd) {
            const commandToAdd = {
                ...originalCmd,
                verb: lang === 'en' ? originalCmd.verbEn : originalCmd.verb,
                level: lang === 'en' ? originalCmd.levelEn : originalCmd.level,
                definition: lang === 'en' ? originalCmd.definitionEn : originalCmd.definition,
                howToAnswer: lang === 'en' ? originalCmd.howToAnswerEn : originalCmd.howToAnswer
            };

            setGuideData({
                ...guideData,
                selectedCommands: [...(guideData.selectedCommands || []), commandToAdd]
            });
            setSelectedCmdId('');
        }
    };

    const updateCommand = (id, field, value) => {
        const newCommands = (guideData.selectedCommands || []).map(cmd => {
            if (cmd.id === id) return { ...cmd, [field]: value };
            return cmd;
        });
        setGuideData({ ...guideData, selectedCommands: newCommands });
    };

    const handleRemoveCommand = (cmdId) => {
        setGuideData({
            ...guideData,
            selectedCommands: (guideData.selectedCommands || []).filter(c => c.id !== cmdId)
        });
    };

    const handlePrint = () => {
        window.print();
    };

    // Função para baixar como DOCX


    // Função para baixar como DOCX nativo
    const handleDownloadDocx = async () => {
        try {
            // Create a new Document
            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        new Paragraph({
                            text: "STUDY GUIDE",
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 200 },
                        }),
                        new Paragraph({
                            text: guideData.subject || "Disciplina",
                            heading: HeadingLevel.HEADING_2,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 100 },
                        }),
                        new Paragraph({
                            text: guideData.title || "Título do Ciclo",
                            heading: HeadingLevel.HEADING_3,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 400 },
                        }),

                        // Loop through topics
                        ...guideData.topics.flatMap(topic => [
                            // Topic Title
                            new Paragraph({
                                text: topic.title || "Tópico",
                                heading: HeadingLevel.HEADING_2,
                                spacing: { before: 400, after: 200 },
                                border: {
                                    bottom: { color: "2563EB", space: 1, value: "single", size: 6 }
                                }
                            }),

                            // Table for Content (2 columns)
                            new Table({
                                width: { size: 100, type: WidthType.PERCENTAGE },
                                borders: {
                                    top: { style: BorderStyle.NONE },
                                    bottom: { style: BorderStyle.NONE },
                                    left: { style: BorderStyle.NONE },
                                    right: { style: BorderStyle.NONE },
                                    insideVertical: { style: BorderStyle.NONE },
                                    insideHorizontal: { style: BorderStyle.NONE },
                                },
                                rows: [
                                    new TableRow({
                                        children: [
                                            // Column 1: Skills
                                            new TableCell({
                                                width: { size: 50, type: WidthType.PERCENTAGE },
                                                children: [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({ text: t.lblChecklist, bold: true, color: "2563EB", size: 24 })
                                                        ],
                                                        spacing: { after: 100 }
                                                    }),
                                                    ...(topic.skills || []).map(skill => new Paragraph({
                                                        text: `• ${skill}`,
                                                        spacing: { after: 50 }
                                                    })),
                                                ],
                                            }),
                                            // Column 2: Resources & Pitfalls
                                            new TableCell({
                                                width: { size: 50, type: WidthType.PERCENTAGE },
                                                children: [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({ text: t.lblResources, bold: true, color: "059669", size: 24 })
                                                        ],
                                                        spacing: { after: 100 }
                                                    }),
                                                    new Paragraph({
                                                        text: topic.resources || "",
                                                        spacing: { after: 200 }
                                                    }),

                                                    // Suggested Practice
                                                    ...(topic.suggestedPractice ? [
                                                        new Paragraph({
                                                            children: [
                                                                new TextRun({ text: t.lblSuggestedPractice || "Suggested Practice", bold: true, color: "9333EA", size: 24 })
                                                            ],
                                                            spacing: { after: 100 }
                                                        }),
                                                        new Paragraph({
                                                            text: topic.suggestedPractice,
                                                            spacing: { after: 200 }
                                                        })
                                                    ] : []),

                                                    // Pitfall
                                                    ...(topic.pitfall ? [
                                                        new Paragraph({
                                                            children: [
                                                                new TextRun({ text: t.lblPitfall, bold: true, color: "DC2626", size: 24 })
                                                            ],
                                                            spacing: { after: 100 }
                                                        }),
                                                        new Paragraph({
                                                            text: `⚠ ${topic.pitfall}`,
                                                            color: "DC2626",
                                                        })
                                                    ] : [])
                                                ],
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new Paragraph({ text: "", spacing: { after: 400 } }) // Spacer
                        ]),
                    ],
                }],
            });

            // Generate and save
            Packer.toBlob(doc).then(blob => {
                saveAs(blob, `${guideData.title || 'StudyGuide'}.docx`);
            });

        } catch (error) {
            console.error("Erro ao gerar DOCX:", error);
            alert("Erro ao gerar DOCX. Verifique o console.");
        }
    };
    const renderContent = (text) => {
        if (!text) return null;

        // Regex to split by LaTeX delimiters $...$
        const parts = text.split(/(\$[^$]+\$)/g);

        return parts.map((part, index) => {
            if (part.startsWith('$') && part.endsWith('$')) {
                const latex = part.slice(1, -1);
                try {
                    const html = window.katex ? window.katex.renderToString(latex, { throwOnError: false }) : latex;
                    return <span key={index} dangerouslySetInnerHTML={{ __html: html }} className="mx-1" />;
                } catch (e) {
                    return <span key={index}>{part}</span>;
                }
            } else {
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const subParts = part.split(urlRegex);
                return subParts.map((subPart, subIndex) => {
                    if (subPart.match(urlRegex)) {
                        return (
                            <a
                                key={`${index}-${subIndex}`}
                                href={subPart}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline break-all hover:text-blue-800"
                            >
                                {subPart}
                            </a>
                        );
                    }
                    return <span key={`${index}-${subIndex}`}>{subPart}</span>;
                });
            }
        });
    };

    const handleDownload = () => {
        if (!window.html2pdf) {
            alert("A biblioteca de PDF ainda está carregando. Tente novamente em alguns segundos.");
            return;
        }

        setIsGenerating(true);
        const element = document.getElementById('print-area');
        const opt = {
            margin: [10, 10, 10, 10], // Margem mínima de segurança para PDF
            filename: `${guideData.subject || 'StudyGuide'}_${guideData.title || 'CycleTest'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2, 
                useCORS: true,
                logging: false,
                letterRendering: true,
                allowTaint: true
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'] }
        };

        window.html2pdf().set(opt).from(element).save().then(() => {
            setIsGenerating(false);
        });
    };

    // --- Renderização ---

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans text-slate-800">

            {/* --- SIDEBAR EDITOR --- */}
            <div className="w-full md:w-5/12 lg:w-4/12 bg-white border-r border-gray-200 p-6 overflow-y-auto h-screen shadow-lg no-print z-10">
                <div className="mb-6 sticky top-0 bg-white z-20 pb-2 border-b border-slate-100 shadow-sm">
                    <h1 className="text-2xl font-bold text-slate-700 mb-2 flex items-center gap-2">
                        <span className="p-2 rounded-lg text-white" style={{ backgroundColor: palette.blue }}>
                            <BookOpen size={20} />
                        </span>
                        {t.editorTitle}
                    </h1>
                    <p className="text-sm text-slate-500 mb-2">{t.editorSubtitle}</p>

                    {/* Math Toolbar */}
                    <div className="flex flex-wrap gap-1 p-2 bg-slate-100 rounded border border-slate-200">
                        {latexSymbols.map((sym, i) => (
                            <button
                                key={i}
                                onClick={() => insertLatex(sym.code)}
                                className="px-2 py-1 bg-white border border-slate-300 rounded text-xs font-mono hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                title={`Inserir ${sym.code}`}
                            >
                                {sym.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* CONFIGURAÇÃO */}
                <div className="space-y-4 mb-8">
                    {/* Language Switcher */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <Languages size={14} /> {t.lblLanguage}
                        </label>
                        <div className="flex gap-2">
                            <button onClick={() => handleSetLang('pt')} className={`flex-1 py-1.5 px-3 rounded text-sm font-semibold transition-colors ${lang === 'pt' ? 'bg-slate-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-100'}`}>Português</button>
                            <button onClick={() => handleSetLang('en')} className={`flex-1 py-1.5 px-3 rounded text-sm font-semibold transition-colors ${lang === 'en' ? 'bg-slate-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-100'}`}>English</button>
                        </div>
                    </div>

                    {/* Grade, Title, Subject */}
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{lang === 'pt' ? 'Série' : 'Grade'}</label>
                            <select
                                value={guideData.grade}
                                onChange={(e) => updateField('grade', e.target.value)}
                                className="w-full p-2 border border-slate-200 rounded focus:ring-2 focus:ring-blue-200 outline-none bg-white font-medium"
                            >
                                <option value="">{lang === 'pt' ? 'Selecione a série' : 'Select grade'}</option>
                                {["6º Ano", "7º Ano", "8º Ano", "9º Ano", "10º Ano", "11º Ano", "12º Ano"].map(g => (
                                    <option key={g} value={g}>{g}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{t.labelTitle}</label>
                            <input
                                type="text"
                                value={guideData.title}
                                onChange={(e) => updateField('title', e.target.value)}
                                className="w-full p-2 border border-slate-200 rounded focus:ring-2 focus:ring-blue-200 outline-none"
                                placeholder={t.placeholderTopic}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{t.labelSubject}</label>
                            <select
                                value={guideData.subject}
                                onChange={(e) => updateField('subject', e.target.value)}
                                className="w-full p-2 border border-slate-200 rounded focus:ring-2 focus:ring-blue-200 outline-none bg-white"
                            >
                                <option value="">{lang === 'pt' ? 'Selecione uma disciplina' : 'Select a subject'}</option>
                                {SUBJECTS_MAPPING.map(sub => (
                                    <option key={sub.pt} value={sub[lang]}>{sub[lang]}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Comandos */}
                    <div className="border-b border-slate-200 pb-6">
                        <h2 className="text-lg font-bold text-slate-700 mb-2">{lang === 'pt' ? 'Comandos de Avaliação' : 'Command Terms'}</h2>
                        <div className="flex gap-2 mb-3">
                            <select value={selectedCmdId} onChange={(e) => setSelectedCmdId(e.target.value)} className="flex-1 p-2 border border-slate-200 rounded text-sm outline-none">
                                <option value="">{lang === 'pt' ? 'Selecione um comando...' : 'Select a command...'}</option>
                                <option value="custom">{lang === 'pt' ? '+ Personalizado' : '+ Custom'}</option>
                                {commandsData.map(cmd => <option key={cmd.id} value={cmd.id}>{lang === 'en' ? cmd.verbEn : cmd.verb}</option>)}
                            </select>
                            <button onClick={handleAddCommand} className="px-3 py-2 bg-slate-600 text-white rounded"><Plus size={16} /></button>
                        </div>
                        {selectedCmdId === 'custom' && (
                            <div className="flex gap-2 mb-3 animate-in fade-in slide-in-from-top-2">
                                <input
                                    type="text"
                                    value={customCmdName}
                                    onChange={(e) => setCustomCmdName(e.target.value)}
                                    placeholder={lang === 'pt' ? 'Digite o nome do comando personalizado...' : 'Type custom command name...'}
                                    className="flex-1 p-2 border border-blue-300 rounded text-sm outline-none focus:ring-2 focus:ring-blue-200"
                                    autoFocus
                                />
                            </div>
                        )}
                        <div className="space-y-3">
                            {(guideData.selectedCommands || []).map(cmd => (
                                <div key={cmd.id} className="p-3 bg-slate-50 border rounded shadow-sm">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-bold text-sm">{cmd.verb} <span className="text-[10px] text-slate-400">({cmd.level})</span></span>
                                        <button onClick={() => handleRemoveCommand(cmd.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                                    </div>
                                    <textarea value={cmd.definition} onChange={(e) => updateCommand(cmd.id, 'definition', e.target.value)} className="w-full text-xs p-1 h-10 border rounded mb-1 bg-white" />
                                    <textarea value={cmd.howToAnswer} onChange={(e) => updateCommand(cmd.id, 'howToAnswer', e.target.value)} className="w-full text-xs p-1 h-10 border rounded bg-white" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tópicos */}
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-bold text-slate-700">Tópicos</h2>
                        <button onClick={addTopic} className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-1 font-bold"><Plus size={16} /> {t.btnNewTopic}</button>
                    </div>

                    {(guideData.topics || []).map((topic, index) => (
                        <div key={topic.id} className="p-4 border rounded-lg bg-white shadow-sm relative group space-y-4">
                            <button onClick={() => removeTopic(topic.id)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                            <input value={topic.title} onChange={(e) => updateTopic(topic.id, 'title', e.target.value)} className="w-full font-bold text-lg border-b border-dashed outline-none" />
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.lblChecklist}</label>
                                {topic.skills.map((skill, sIdx) => (
                                    <div key={sIdx} className="flex gap-2 items-center mb-1">
                                        <input value={skill} onChange={(e) => updateSkill(topic.id, sIdx, e.target.value)} className="flex-1 text-sm border-b focus:border-blue-400 outline-none" />
                                        <button onClick={() => removeSkill(topic.id, sIdx)} className="text-slate-300">&times;</button>
                                    </div>
                                ))}
                                <button onClick={() => addSkill(topic.id)} className="text-[10px] text-blue-500 font-bold hover:underline">+ ADD SKILL</button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase">{t.lblResources}</label>
                                    <textarea value={topic.resources} onChange={(e) => updateTopic(topic.id, 'resources', e.target.value)} className="w-full text-xs p-2 border rounded h-16 bg-slate-50" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase">{t.lblSuggestedPractice}</label>
                                    <textarea value={topic.suggestedPractice} onChange={(e) => updateTopic(topic.id, 'suggestedPractice', e.target.value)} className="w-full text-xs p-2 border rounded h-16 bg-slate-50" />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase">{t.lblPitfall}</label>
                                    <textarea value={topic.pitfall} onChange={(e) => updateTopic(topic.id, 'pitfall', e.target.value)} className="w-full text-xs p-2 border border-orange-100 bg-orange-50 rounded h-16" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* BOTÕES DE AÇÃO */}
                <div className="mt-8 pt-8 border-t border-slate-100 space-y-6">
                    {/* Ações Principais */}
                    <div className="grid grid-cols-3 gap-3">
                        <button 
                            onClick={handleNewGuide} 
                            className="h-12 flex items-center justify-center gap-2 bg-white text-slate-600 rounded-xl text-sm font-bold border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95"
                        >
                            <Plus size={18} className="text-slate-400" />
                            <span>Novo</span>
                        </button>
                        
                        <button 
                            onClick={saveStudyGuide} 
                            disabled={isSaving} 
                            className="h-12 flex items-center justify-center gap-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
                        >
                            {isSaving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                            <span>{editingId ? 'Atualizar' : 'Salvar'}</span>
                        </button>
                        
                        <button 
                            onClick={() => setShowHistory(true)} 
                            className="h-12 flex items-center justify-center gap-2 bg-slate-800 text-white rounded-xl text-sm font-bold shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all active:scale-95"
                        >
                            <Cloud size={18} className="text-slate-400" />
                            <span>Banco</span>
                        </button>
                    </div>

                    {/* Exportação e Impressão */}
                    <div className="grid grid-cols-2 gap-3">
                        <button 
                            onClick={handleDownload} 
                            className="h-12 flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95"
                        >
                            <FileDown size={20} />
                            <span>PDF</span>
                        </button>
                        
                        <button 
                            onClick={handleDownloadDocx} 
                            className="h-12 flex items-center justify-center gap-2 bg-blue-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all active:scale-95"
                        >
                            <FileText size={20} />
                            <span>DOCX</span>
                        </button>
                        
                        <button 
                            onClick={handlePrint} 
                            className="h-12 flex items-center justify-center gap-2 border-2 border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95 col-span-2"
                        >
                            <Printer size={20} className="text-slate-400" />
                            <span>Imprimir Study Guide</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* PREVIEW */}
            <div className="flex-1 bg-gray-200 p-4 overflow-y-auto flex justify-center items-start print-container">
                <div id="print-area" className="bg-white w-full max-w-[210mm] p-[10mm] relative print-sheet text-slate-800">
                    <header className="mb-6 border-b pb-4 flex items-center justify-between print-only-header" style={{ borderColor: palette.blue }}>
                        <div className="flex items-center gap-4">
                            <img src="https://i.postimg.cc/Vs77fcRR/Logo_Escola_Eleva_Barra_Horizontal_Inspired.png" alt="Logo" className="h-10" />
                            <div className="h-6 w-px bg-slate-300"></div>
                            <img src="https://i.postimg.cc/RZwmwktT/Chat_GPT_Image_27_de_jan_de_2026_08_01_26.png" alt="Mascote" className="h-10" />
                        </div>
                        <div className="text-right">
                            <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">Study Guide</h2>
                            <h1 className="text-xl font-black text-slate-800 leading-tight" style={{ color: palette.blue }}>{guideData.subject}</h1>
                            <p className="text-xs font-bold text-slate-500">{guideData.title} {guideData.grade && `(${guideData.grade})`}</p>
                        </div>
                    </header>

                    {(guideData.selectedCommands || []).length > 0 && (
                        <div className="mb-6 space-y-3 break-inside-avoid shadow-inner p-4 bg-slate-50/50 rounded-xl">
                            <h3 className="text-[10px] font-black uppercase text-slate-400 border-b pb-1 tracking-tighter">Essential Command Terms</h3>
                            {guideData.selectedCommands.map(cmd => (
                                <div key={cmd.id} className="grid grid-cols-12 gap-3 text-xs leading-none">
                                    <div className="col-span-3 font-black text-slate-800 uppercase italic">{cmd.verb}</div>
                                    <div className="col-span-4 text-slate-600 italic font-medium">{cmd.definition}</div>
                                    <div className="col-span-5 text-blue-600 font-bold">{cmd.howToAnswer}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="topics-container">
                        {guideData.topics.map((topic, index) => (
                            <section key={topic.id} className="topic-card mb-8 border-l-4 p-4 bg-slate-50/20 break-inside-avoid" style={{ borderColor: palette.purple }}>
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-xs">{index + 1}</span>
                                    <h3 className="text-base font-black text-slate-800 uppercase tracking-tighter italic">{topic.title}</h3>
                                </div>
                                <div className="space-y-4 ml-8">
                                    <div>
                                        <h4 className="text-[10px] font-black text-blue-500 uppercase flex items-center gap-1 mb-2">Learning Objectives</h4>
                                        <ul className="space-y-2">
                                            {topic.skills.map((skill, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-xs font-semibold text-slate-700">
                                                    <div className="w-3 h-3 rounded-sm border-2 border-slate-300 mt-0.5" />
                                                    <span>{skill}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-3 bg-white border rounded shadow-sm overflow-hidden min-w-0">
                                            <span className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Resources</span>
                                            <div className="text-[11px] font-medium text-slate-600 whitespace-pre-wrap break-all">
                                                {renderContent(topic.resources)}
                                            </div>
                                        </div>
                                        {topic.suggestedPractice && (
                                            <div className="p-3 bg-white border border-indigo-100 rounded shadow-sm overflow-hidden min-w-0">
                                                <span className="text-[9px] font-bold text-indigo-400 uppercase block mb-1">Suggested Practice</span>
                                                <div className="text-[11px] font-medium text-slate-600 whitespace-pre-wrap break-all">
                                                    {renderContent(topic.suggestedPractice)}
                                                </div>
                                            </div>
                                        )}
                                        {topic.pitfall && (
                                            <div className="p-3 bg-orange-50 border border-orange-100 rounded shadow-sm col-span-2 overflow-hidden">
                                                <span className="text-[9px] font-bold text-orange-400 uppercase block mb-1">Common Pitfall</span>
                                                <div className="text-[11px] italic text-slate-700 font-medium whitespace-pre-wrap break-all">
                                                    {renderContent(topic.pitfall)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </div>

            {showHistory && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="p-5 border-b flex justify-between items-center bg-slate-50 rounded-t-2xl">
                            <h2 className="text-xl font-black text-slate-800 uppercase">Banco de Study Guides</h2>
                            <button onClick={() => setShowHistory(false)}><X /></button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50">
                            {/* Filtros */}
                            <div className="flex gap-2 mb-6 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                                <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase uppercase uppercase">Disciplina</label>
                                    <select 
                                        value={filterSubject} 
                                        onChange={(e) => setFilterSubject(e.target.value)}
                                        className="w-full text-xs p-2 border rounded bg-slate-50 outline-none"
                                    >
                                        <option value="">{lang === 'pt' ? 'Todas as Disciplinas' : 'All Subjects'}</option>
                                        {SUBJECTS_MAPPING.map(sub => (
                                            <option key={sub.pt} value={sub[lang]}>{sub[lang]}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase">Série</label>
                                    <select 
                                        value={filterGrade} 
                                        onChange={(e) => setFilterGrade(e.target.value)}
                                        className="w-full text-xs p-2 border rounded bg-slate-50 outline-none"
                                    >
                                        <option value="">Todas as Séries</option>
                                        {["6º Ano", "7º Ano", "8º Ano", "9º Ano", "10º Ano", "11º Ano", "12º Ano"].map(g => (
                                            <option key={g} value={g}>{g}</option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    onClick={() => { setFilterSubject(''); setFilterGrade(''); }}
                                    className="self-end p-2 text-slate-400 hover:text-slate-600"
                                    title="Limpar Filtros"
                                >
                                    <RefreshCw size={16} />
                                </button>
                            </div>

                            {isLoadingHistory ? <RefreshCw className="animate-spin mx-auto mt-10 text-blue-500" /> : (
                                <div className="space-y-3">
                                    {savedGuides
                                        .filter(g => !filterSubject || g.subject === filterSubject)
                                        .filter(g => !filterGrade || g.grade === filterGrade)
                                        .map(guide => (
                                        <div key={guide.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center cursor-pointer hover:border-blue-400 shadow-sm transition-all" onClick={() => loadStudyGuide(guide)}>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[10px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded uppercase tracking-tighter">{guide.grade}</span>
                                                    <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-0.5 rounded uppercase tracking-tighter">{guide.subject}</span>
                                                </div>
                                                <h4 className="font-bold text-slate-800 leading-tight">{guide.title}</h4>
                                                <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold flex items-center gap-1">
                                                    <BookOpen size={10} /> Por: {guide.updatedBy}
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={(e) => { e.stopPropagation(); deleteStudyGuide(guide.id); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                                                <button className="px-5 py-2 bg-slate-800 text-white rounded-lg font-black text-xs hover:bg-slate-700 shadow-md">ABRIR</button>
                                            </div>
                                        </div>
                                    ))}
                                    {savedGuides.filter(g => (!filterSubject || g.subject === filterSubject) && (!filterGrade || g.grade === filterGrade)).length === 0 && (
                                        <div className="text-center py-12 text-slate-400">
                                            <Cloud className="mx-auto mb-2 opacity-20" size={48} />
                                            <p className="text-sm font-medium">Nenhum guia encontrado para os filtros selecionados.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @media print {
                  @page { 
                    margin: 0; 
                    size: a4;
                  }
                  body { 
                    background: white !important; 
                    margin: 0 !important;
                    padding: 0 !important;
                    -webkit-print-color-adjust: exact;
                  }
                  .no-print { display: none !important; }
                  .print-container { 
                    background: white !important; 
                    padding: 0 !important; 
                    display: block !important;
                    height: auto !important;
                  }
                  .print-sheet { 
                    box-shadow: none !important; 
                    padding: 15mm !important; 
                    width: 100% !important; 
                    max-width: none !important; 
                    border: none !important;
                    min-height: 0 !important;
                  }
                  .topics-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                  .topic-card { break-inside: avoid !important; }
                  
                  /* Garantir que links e textos longos quebrem corretamente */
                  a, p, div {
                    word-break: break-all !important;
                    overflow-wrap: break-word !important;
                  }
                  
                  /* Garantir que o conteúdo comece no topo */
                  header:first-child { margin-top: 0 !important; }
                }
            `}</style>
        </div>
    );
};

export default StudyGuideGenerator;
